import { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/modules/auth/hooks/useAuth';
import { supabase } from '@/supabaseClient';
import * as Sentry from '@sentry/browser';

/**
 * Hook to manage display name setup for new users
 * Determines if a user needs to set up their display name
 */
export function useDisplayNameSetup() {
  const { user, loading: authLoading } = useAuth();
  const [needsSetup, setNeedsSetup] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  const [profile, setProfile] = useState(null);
  const isCheckingRef = useRef(false);

  useEffect(() => {
    if (authLoading || !user) {
      setNeedsSetup(false);
      setProfile(null);
      return;
    }

    // Prevent duplicate API calls in React StrictMode
    if (isCheckingRef.current) {
      return;
    }

    const checkDisplayNameStatus = async () => {
      console.log('Checking if user needs display name setup');
      isCheckingRef.current = true;
      setIsChecking(true);
      
      try {
        const { data: { session } } = await supabase.auth.getSession();
        
        const response = await fetch('/api/profile', {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${session?.access_token}`,
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const profileData = await response.json();
        
        setProfile(profileData);
        
        // User needs setup if they don't have a display name set
        const needsDisplayNameSetup = !profileData.displayName || profileData.displayName.trim() === '';
        setNeedsSetup(needsDisplayNameSetup);
        
        console.log('User needs display name setup:', needsDisplayNameSetup);
      } catch (error) {
        console.error('Error checking display name status:', error);
        Sentry.captureException(error, {
          extra: { 
            userEmail: user.email,
            context: 'display-name-setup-check'
          }
        });
        // On error, assume setup is needed to ensure user can't bypass requirement
        setNeedsSetup(true);
      } finally {
        setIsChecking(false);
        isCheckingRef.current = false;
      }
    };

    checkDisplayNameStatus();
  }, [user, authLoading]);

  const markSetupComplete = (updatedProfile) => {
    // console.log('Display name setup completed:', updatedProfile);
    setProfile(updatedProfile);
    setNeedsSetup(false);
  };

  return {
    needsSetup,
    isChecking,
    profile,
    markSetupComplete,
  };
}